/*
 * Description:
 * ---------------------
 * Access Control panel — portal accounts, domains, and which edge belongs to
 * which domain.
 *
 * A super admin sees everything and is the only role that can create domains
 * or move an edge between them. A domain admin sees and manages only accounts
 * inside its own domains. Every rule here is enforced again server-side in
 * server/pkg/portalauth — the UI hides what an account cannot do, it does not
 * decide it.
 *
 * Onboarding is username + temporary password: every password set from this
 * panel is a one-time credential, and the account it belongs to can do nothing
 * but replace it at first sign-in (portalauth.Auth.Require). Where the server
 * sends email, an admin can instead invite someone by email, who then chooses
 * their own. A box joins an organisation by the code that came with it, which
 * any admin may type in; only a super admin moves boxes between organisations.
 * A laptop joins by a one-time code an admin makes here, and an admin switches
 * off a laptop or phone that is lost (EnrolledDevices.js); its people sign in
 * on it with their organisation's Microsoft accounts (SignInSettings.js).
 * New wording lives in accessWords.js.
 *
 * Styling lives in the .ac-* block of AppLayout.css, alongside .qz-* (QoS).
 *
 * ! file AccessPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiUsers,
  FiPlus,
  FiTrash2,
  FiKey,
  FiRefreshCw,
  FiShield,
  FiGlobe,
  FiServer,
  FiSlash,
  FiCheckCircle,
  FiMail,
  FiBox,
} from "react-icons/fi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { useToast } from "./ToastProvider";
import { useConfirm } from "./ConfirmModal";
import { useSession, minPassword } from "./PortalGate";
import EnrolledDevices from "./EnrolledDevices";
import SignInSettings from "./SignInSettings";
import SkipSettings from "./SkipSettings";
import CheckSettings from "./CheckSettings";
import * as W from "./accessWords";

const { ROLES, roleLabel } = W;

// Sending an invitation waits on the server's mail relay, which it gives 45 s.
const MAIL_MS = 50000;

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "never");

export default function AccessPanel({ serverApiUrl, nodes }) {
  const session = useSession();
  const toast = useToast();
  const confirm = useConfirm();

  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [invitations, setInvitations] = useState([]);
  // null until a server answers that it enrolls devices.
  const [enrollments, setEnrollments] = useState(null);
  // null until a server answers that people sign in on its devices.
  const [signIns, setSignIns] = useState(null);
  // null until a server answers with what its organisations ask of laptops.
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const canManageDomains = session.isSuperAdmin;

  const load = useCallback(async () => {
    setError("");
    try {
      const [uRes, dRes, iRes, eRes, sRes, pRes] = await Promise.all([
        fetchWithTimeout(serverApiUrl("/users"), { credentials: "same-origin" }),
        fetchWithTimeout(serverApiUrl("/domains"), { credentials: "same-origin" }),
        fetchWithTimeout(serverApiUrl("/invites"), { credentials: "same-origin" }),
        fetchWithTimeout(serverApiUrl("/enrollments"), { credentials: "same-origin" }),
        fetchWithTimeout(serverApiUrl("/signin"), { credentials: "same-origin" }),
        fetchWithTimeout(serverApiUrl("/devicepolicy"), { credentials: "same-origin" }),
      ]);
      if (uRes.ok) {
        const data = await uRes.json();
        setUsers(data.users || []);
      } else {
        setError(`Could not load accounts (${uRes.status}).`);
      }
      if (dRes.ok) {
        const data = await dRes.json();
        setDomains(data.domains || []);
      }
      // A server older than invitations answers 404, and none are shown.
      if (iRes.ok) {
        const data = await iRes.json();
        setInvitations(
          (data.invitations || []).sort((a, b) => a.email.localeCompare(b.email)),
        );
      }
      // A server older than enrollment answers 404, and its section is not
      // shown: it cannot switch a device off either.
      if (eRes.ok) {
        const data = await eRes.json();
        setEnrollments(
          (data.enrollments || []).sort((a, b) => a.label.localeCompare(b.label)),
        );
      }
      if (sRes.ok) {
        const data = await sRes.json();
        setSignIns(data.domains || []);
      }
      if (pRes.ok) {
        setPolicies(await pRes.json());
      }
    } catch (err) {
      setError(err.message || "Could not reach the control plane.");
    } finally {
      setLoading(false);
    }
  }, [serverApiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  // An admin below super admin works only inside the organisations it holds.
  const grantableDomains = useMemo(
    () =>
      session.isSuperAdmin
        ? domains
        : domains.filter((d) => session.domains.includes(d.id)),
    [domains, session],
  );

  // send issues a JSON call and surfaces the server's own error text, which
  // carries the specific rule that rejected the request.
  const send = useCallback(
    async (path, method, body, timeoutMs = 10000) => {
      setBusy(true);
      try {
        const res = await fetchWithTimeout(
          serverApiUrl(path),
          {
            method,
            credentials: "same-origin",
            headers: body ? { "Content-Type": "application/json" } : undefined,
            body: body ? JSON.stringify(body) : undefined,
          },
          timeoutMs,
        );
        if (res.status === 204) return { ok: true };
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(W.sentence(data.error) || `Request failed (${res.status})`);
        }
        return { ok: true, data };
      } finally {
        setBusy(false);
      }
    },
    [serverApiUrl],
  );

  return (
    <div className="ac-stack">
      <div className="panel">
        <div className="ac-head">
          <div>
            <div className="panel-title">
              <FiShield style={{ verticalAlign: "-2px", marginRight: 8 }} />
              Access Control
            </div>
            <div className="panel-sub">
              Signed in as <strong>{session.user?.username}</strong> ·{" "}
              {roleLabel(session.role)}
              {!session.isSuperAdmin && session.domains.length > 0 && (
                <> · {session.domains.join(", ")}</>
              )}
            </div>
          </div>
          <button type="button" className="ac-btn" onClick={load} disabled={busy}>
            <FiRefreshCw size={14} /> Refresh
          </button>
        </div>
        {error && (
          <div className="panel-error" style={{ marginTop: 10 }}>
            {error}
          </div>
        )}
      </div>

      {canManageDomains && (
        <DomainsSection
          domains={domains}
          send={send}
          busy={busy}
          reload={load}
          toast={toast}
          confirm={confirm}
        />
      )}

      <UsersSection
        users={users}
        grantableDomains={grantableDomains}
        loading={loading}
        session={session}
        send={send}
        busy={busy}
        reload={load}
        toast={toast}
        confirm={confirm}
      />

      <InvitationsSection
        invitations={invitations}
        domains={domains}
        grantableDomains={grantableDomains}
        session={session}
        send={send}
        busy={busy}
        reload={load}
        toast={toast}
        confirm={confirm}
      />

      <ClaimSection
        domains={domains}
        grantableDomains={grantableDomains}
        send={send}
        busy={busy}
        toast={toast}
      />

      {enrollments && (
        <EnrolledDevices
          nodes={nodes}
          enrollments={enrollments}
          domains={domains}
          grantableDomains={grantableDomains}
          send={send}
          busy={busy}
          reload={load}
          toast={toast}
          confirm={confirm}
        />
      )}

      {signIns && signIns.length > 0 && (
        <SignInSettings
          orgs={signIns}
          send={send}
          busy={busy}
          reload={load}
          toast={toast}
          confirm={confirm}
        />
      )}

      {policies && policies.domains?.length > 0 && (
        <SkipSettings
          orgs={policies.domains}
          services={policies.services || {}}
          send={send}
          busy={busy}
          reload={load}
          toast={toast}
        />
      )}

      {policies && policies.domains?.length > 0 && (
        <CheckSettings orgs={policies.domains} send={send} busy={busy} reload={load} toast={toast} />
      )}

      {canManageDomains && (
        <NodeAssignmentSection
          nodes={nodes}
          domains={domains}
          send={send}
          busy={busy}
          toast={toast}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DOMAINS
   ═══════════════════════════════════════════════════════════════════════════ */

function DomainsSection({ domains, send, busy, reload, toast, confirm }) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");

  const create = async (e) => {
    e.preventDefault();
    try {
      await send("/domains", "POST", {
        id: id.trim().toLowerCase(),
        name: name.trim(),
      });
      toast.success(`Domain "${id.trim().toLowerCase()}" created`);
      setId("");
      setName("");
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({
      title: `Delete domain "${d.id}"?`,
      message:
        "Edges in this domain become unassigned and visible only to super admins. Accounts scoped to it lose that access and are signed out.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await send(`/domains/${encodeURIComponent(d.id)}`, "DELETE");
      toast.success(`Domain "${d.id}" deleted`);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="panel">
      <div className="panel-title">
        <FiGlobe style={{ verticalAlign: "-2px", marginRight: 8 }} />
        Domains
      </div>
      <div className="panel-sub">
        A domain is a customer boundary. Accounts are scoped to domains, and
        each edge belongs to at most one.
      </div>

      <form onSubmit={create} className="ac-form">
        <input
          className="ac-input narrow"
          placeholder="domain-id"
          value={id}
          onChange={(e) => setId(e.target.value)}
          autoCapitalize="none"
          spellCheck="false"
        />
        <input
          className="ac-input narrow"
          placeholder="Display name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          className="ac-btn primary"
          disabled={busy || !id.trim()}
        >
          <FiPlus size={14} /> Add domain
        </button>
      </form>

      {domains.length === 0 ? (
        <div className="panel-note">
          No domains yet. Until an edge is assigned to one, only super admins
          can see it.
        </div>
      ) : (
        <div className="ac-list">
          {domains.map((d) => (
            <div key={d.id} className="ac-row">
              <div className="ac-row-main">
                <div className="ac-row-title">{d.name || d.id}</div>
                <div className="ac-row-meta">
                  {d.id} · created {fmtDate(d.created_at)}
                </div>
              </div>
              <button
                type="button"
                className="ac-btn danger icon"
                onClick={() => remove(d)}
                disabled={busy}
                title="Delete domain"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   USERS
   ═══════════════════════════════════════════════════════════════════════════ */

function UsersSection({
  users,
  grantableDomains,
  loading,
  session,
  send,
  busy,
  reload,
  toast,
  confirm,
}) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    username: "",
    password: "",
    role: "viewer",
    domains: [],
  });

  // A domain admin can only grant roles at or below its own, and only inside
  // the domains it holds.
  const grantableRoles = useMemo(
    () =>
      session.isSuperAdmin ? ROLES : ROLES.filter((r) => r.key !== "super_admin"),
    [session.isSuperAdmin],
  );

  const reset = () =>
    setForm({ username: "", password: "", role: "viewer", domains: [] });

  const create = async (e) => {
    e.preventDefault();
    const need = minPassword(form.role);
    if (form.password.length < need) {
      toast.error(`Password must be at least ${need} characters for this role.`);
      return;
    }
    if (form.role !== "super_admin" && form.domains.length === 0) {
      toast.error(W.PICK_AN_ORG);
      return;
    }
    try {
      await send("/users", "POST", {
        username: form.username.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        domains: form.role === "super_admin" ? [] : form.domains,
      });
      toast.success(
        `Account "${form.username.trim().toLowerCase()}" created — they set their own password at first sign-in.`,
      );
      reset();
      setShowNew(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleDisabled = async (u) => {
    const disabling = !u.disabled;
    if (disabling) {
      const ok = await confirm({
        title: `Disable "${u.username}"?`,
        message:
          "The account is signed out immediately and cannot sign back in until it is re-enabled.",
        confirmLabel: "Disable",
        variant: "danger",
      });
      if (!ok) return;
    }
    try {
      await send(`/users/${encodeURIComponent(u.username)}`, "PATCH", {
        disabled: disabling,
      });
      toast.success(`${u.username} ${disabling ? "disabled" : "enabled"}`);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await send(`/users/${encodeURIComponent(u.username)}`, "PATCH", { role });
      toast.success(`${u.username} is now ${roleLabel(role)}`);
      reload();
    } catch (err) {
      toast.error(err.message);
      reload();
    }
  };

  const changeDomains = async (u, domainIds) => {
    try {
      await send(`/users/${encodeURIComponent(u.username)}`, "PATCH", {
        domains: domainIds,
      });
      toast.success(W.orgsUpdated(u.username));
      reload();
    } catch (err) {
      toast.error(err.message);
      reload();
    }
  };

  const resetPassword = async (u) => {
    const pw = window.prompt(
      `Temporary password for "${u.username}" (at least ${minPassword(u.role)} characters). They will be asked to change it at their next sign-in:`,
    );
    if (!pw) return;
    try {
      await send(`/users/${encodeURIComponent(u.username)}/password`, "POST", {
        password: pw,
      });
      toast.success(
        `Password reset for ${u.username} — they are signed out and must set a new one.`,
      );
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const remove = async (u) => {
    const ok = await confirm({
      title: `Delete "${u.username}"?`,
      message: "The account and its sessions are removed. This cannot be undone.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await send(`/users/${encodeURIComponent(u.username)}`, "DELETE");
      toast.success(`Account "${u.username}" deleted`);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="panel">
      <div className="ac-head">
        <div>
          <div className="panel-title">
            <FiUsers style={{ verticalAlign: "-2px", marginRight: 8 }} />
            Accounts
          </div>
          <div className="panel-sub">
            {users.length} account{users.length === 1 ? "" : "s"} you can manage.
          </div>
        </div>
        <button
          type="button"
          className="ac-btn primary"
          onClick={() => setShowNew((v) => !v)}
          disabled={busy}
        >
          <FiPlus size={14} /> {showNew ? "Cancel" : "New account"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="ac-newform">
          <div className="ac-grid">
            <label className="ac-field">
              <span>Username</span>
              <input
                className="ac-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="jane.doe"
                autoCapitalize="none"
                spellCheck="false"
              />
            </label>
            <label className="ac-field">
              <span>Temporary password</span>
              <input
                className="ac-input"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={`At least ${minPassword(form.role)} characters`}
                autoComplete="off"
                spellCheck="false"
              />
            </label>
            <label className="ac-field">
              <span>Role</span>
              <select
                className="ac-input"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {grantableRoles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="ac-blurb">
            {ROLES.find((r) => r.key === form.role)?.blurb}
          </div>
          <div className="ac-blurb">
            Hand the username and this password to the account holder. It works
            once: their first sign-in lands on a change-password screen and
            nothing else opens until they set their own.
          </div>

          {form.role !== "super_admin" && (
            <div className="ac-field">
              <span>{W.ORGANISATIONS}</span>
              {grantableDomains.length === 0 ? (
                <div className="panel-note">{W.NO_ORGS}</div>
              ) : (
                <div className="ac-chips">
                  {grantableDomains.map((d) => {
                    const on = form.domains.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        className={`ac-chip ${on ? "on" : ""}`}
                        onClick={() =>
                          setForm({
                            ...form,
                            domains: on
                              ? form.domains.filter((x) => x !== d.id)
                              : [...form.domains, d.id],
                          })
                        }
                      >
                        {d.name || d.id}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div>
            <button
              type="submit"
              className="ac-btn primary"
              disabled={busy || !form.username.trim() || !form.password}
            >
              Create account
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="panel-note">Loading accounts…</div>
      ) : users.length === 0 ? (
        <div className="panel-note">No accounts to show.</div>
      ) : (
        <div className="ac-list">
          {users.map((u) => {
            const isSelf = u.username === session.user?.username;
            return (
              <div key={u.username} className="ac-row">
                <div className="ac-row-main">
                  <div className="ac-row-title">
                    {u.username}
                    {isSelf && <span className="ac-tag self">you</span>}
                    {u.disabled && <span className="ac-tag off">disabled</span>}
                    {u.must_change_password && (
                      <span
                        className="ac-tag temp"
                        title="Signs in with a temporary password and must replace it before anything else"
                      >
                        temp password
                      </span>
                    )}
                  </div>
                  <div className="ac-row-meta">
                    {W.userEmail(u)} · last sign-in {fmtDate(u.last_login)} ·
                    created {fmtDate(u.created_at)}
                  </div>
                  <div className="ac-row-controls">
                    <select
                      className="ac-input narrow"
                      value={u.role}
                      disabled={busy || isSelf}
                      title={
                        isSelf ? "You cannot change your own role" : "Change role"
                      }
                      onChange={(e) => changeRole(u, e.target.value)}
                    >
                      {grantableRoles.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.label}
                        </option>
                      ))}
                    </select>

                    {u.role !== "super_admin" && (
                      <div className="ac-chips">
                        {grantableDomains.map((d) => {
                          const on = (u.domains || []).includes(d.id);
                          return (
                            <button
                              key={d.id}
                              type="button"
                              className={`ac-chip sm ${on ? "on" : ""}`}
                              disabled={busy}
                              onClick={() =>
                                changeDomains(
                                  u,
                                  on
                                    ? (u.domains || []).filter((x) => x !== d.id)
                                    : [...(u.domains || []), d.id],
                                )
                              }
                            >
                              {d.id}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ac-row-actions">
                  <button
                    type="button"
                    className="ac-btn icon"
                    onClick={() => resetPassword(u)}
                    disabled={busy}
                    title="Reset password"
                  >
                    <FiKey size={14} />
                  </button>
                  <button
                    type="button"
                    className="ac-btn icon"
                    onClick={() => toggleDisabled(u)}
                    disabled={busy || isSelf}
                    title={
                      isSelf
                        ? "You cannot disable yourself"
                        : u.disabled
                          ? "Enable"
                          : "Disable"
                    }
                  >
                    {u.disabled ? (
                      <FiCheckCircle size={14} />
                    ) : (
                      <FiSlash size={14} />
                    )}
                  </button>
                  <button
                    type="button"
                    className="ac-btn danger icon"
                    onClick={() => remove(u)}
                    disabled={busy || isSelf}
                    title={isSelf ? "You cannot delete yourself" : "Delete account"}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   INVITATIONS
   ═══════════════════════════════════════════════════════════════════════════ */

// An invitation is an email with a link: whoever opens it chooses their own
// username and password, and the account gets the address it went to. The
// pending ones stay listed, so an admin can withdraw a link before it is used.
function InvitationsSection({
  invitations,
  domains,
  grantableDomains,
  session,
  send,
  busy,
  reload,
  toast,
  confirm,
}) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ email: "", domain: "", role: "viewer" });

  // At most the inviter's own role, and never super admin.
  const roles = ROLES.slice(0, ROLES.findIndex((r) => r.key === session.role) + 1).filter(
    (r) => r.key !== "super_admin",
  );
  const domain = form.domain || grantableDomains[0]?.id || "";
  const orgName = (id) => domains.find((d) => d.id === id)?.name || id;

  const invite = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    try {
      await send("/invites", "POST", { email, domain, role: form.role }, MAIL_MS);
      toast.success(W.invited(email));
      setForm({ email: "", domain: "", role: "viewer" });
      setShowNew(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const withdraw = async (inv) => {
    const ok = await confirm({
      title: `${W.withdrawLabel(inv.email)}?`,
      message: W.WITHDRAW_MEANS,
      confirmLabel: W.WITHDRAW,
      variant: "danger",
    });
    if (!ok) return;
    try {
      await send(`/invites/${encodeURIComponent(inv.id)}`, "DELETE");
      toast.success(W.withdrawn(inv.email));
      reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Without email there is nothing to send, and nothing to show until an
  // invitation sent earlier is waiting.
  if (!session.sendsEmail && invitations.length === 0) return null;
  return (
    <div className="panel">
      <div className="ac-head">
        <div>
          <div className="panel-title">
            <FiMail style={{ verticalAlign: "-2px", marginRight: 8 }} />
            {W.INVITES_TITLE}
          </div>
          <div className="panel-sub">
            {session.sendsEmail ? W.INVITES_LEAD : W.INVITES_NO_EMAIL}
          </div>
        </div>
        {session.sendsEmail && (
          <button
            type="button"
            className="ac-btn primary"
            onClick={() => setShowNew((v) => !v)}
            disabled={busy}
          >
            <FiPlus size={14} /> {showNew ? "Cancel" : W.INVITE}
          </button>
        )}
      </div>

      {showNew &&
        (grantableDomains.length === 0 ? (
          <div className="panel-note">{W.NO_ORGS}</div>
        ) : (
          <form onSubmit={invite} className="ac-newform">
            <div className="ac-grid">
              <label className="ac-field">
                <span>{W.EMAIL}</span>
                <input
                  className="ac-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@company.com"
                  autoComplete="off"
                  spellCheck="false"
                />
              </label>
              <label className="ac-field">
                <span>{W.ORGANISATION}</span>
                <select
                  className="ac-input"
                  value={domain}
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                >
                  {grantableDomains.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name || d.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ac-field">
                <span>{W.ROLE}</span>
                <select
                  className="ac-input"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                >
                  {roles.map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="ac-blurb">
              {ROLES.find((r) => r.key === form.role)?.blurb}
            </div>
            <div>
              <button
                type="submit"
                className="ac-btn primary"
                disabled={busy || !form.email.trim()}
              >
                {busy ? W.SENDING : W.INVITE_SEND}
              </button>
            </div>
          </form>
        ))}

      {invitations.length === 0 ? (
        <div className="panel-note">{W.NO_INVITES}</div>
      ) : (
        <div className="ac-list">
          {invitations.map((inv) => (
            <div key={inv.id} className="ac-row">
              <div className="ac-row-main">
                <div className="ac-row-title">{inv.email}</div>
                <div className="ac-row-meta">
                  {W.inviteMeta(inv, orgName(inv.domain), fmtDate(inv.expires))}
                </div>
              </div>
              <button
                type="button"
                className="ac-btn danger icon"
                onClick={() => withdraw(inv)}
                disabled={busy}
                title={W.withdrawLabel(inv.email)}
                aria-label={W.withdrawLabel(inv.email)}
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ADD A BOX BY ITS CODE
   ═══════════════════════════════════════════════════════════════════════════ */

// The code is the box's own proof of who holds it: whoever types it in may put
// a box no organisation holds yet into one of theirs. App.js re-polls /nodes
// every 3s, so the box shows up in the lists on its own.
function ClaimSection({ domains, grantableDomains, send, busy, toast }) {
  const [code, setCode] = useState("");
  const [domain, setDomain] = useState("");
  const chosen = domain || grantableDomains[0]?.id || "";

  const claim = async (e) => {
    e.preventDefault();
    try {
      const { data } = await send("/claims", "POST", { code: code.trim(), domain: chosen });
      const org = domains.find((d) => d.id === data.domain);
      toast.success(W.claimed(data.node_id, org?.name || data.domain));
      setCode("");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="panel">
      <div className="panel-title">
        <FiBox style={{ verticalAlign: "-2px", marginRight: 8 }} />
        {W.CLAIM_TITLE}
      </div>
      <div className="panel-sub">{W.CLAIM_LEAD}</div>
      {grantableDomains.length === 0 ? (
        <div className="panel-note">{W.NO_ORGS}</div>
      ) : (
        <form onSubmit={claim} className="ac-form">
          <input
            className="ac-input narrow ac-code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={W.BOX_CODE_EXAMPLE}
            aria-label={W.BOX_CODE}
            autoComplete="off"
            spellCheck="false"
          />
          <select
            className="ac-input narrow"
            value={chosen}
            onChange={(e) => setDomain(e.target.value)}
            aria-label={W.ORGANISATION}
          >
            {grantableDomains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name || d.id}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="ac-btn primary"
            disabled={busy || !code.trim()}
          >
            <FiPlus size={14} /> {W.CLAIM}
          </button>
        </form>
      )}
      <div className="ac-blurb">{W.BOX_CODE_WHERE}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   NODE → DOMAIN ASSIGNMENT
   ═══════════════════════════════════════════════════════════════════════════ */

function NodeAssignmentSection({ nodes, domains, send, busy, toast }) {
  const [pending, setPending] = useState(null);

  // App.js re-polls /nodes every 3s, so the assignment shown here catches up
  // on its own rather than needing a refresh hook threaded back up.
  const assign = async (nodeId, domain) => {
    setPending(nodeId);
    try {
      await send(`/nodes/${encodeURIComponent(nodeId)}/domain`, "PUT", {
        domain,
      });
      toast.success(
        domain
          ? `${nodeId} assigned to "${domain}"`
          : `${nodeId} is now unassigned`,
      );
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="panel">
      <div className="panel-title">
        <FiServer style={{ verticalAlign: "-2px", marginRight: 8 }} />
        Edge Assignment
      </div>
      <div className="panel-sub">
        Which domain each edge belongs to. Unassigned edges are visible only to
        super admins — an edge is never placed in a domain by its own
        registration.
      </div>

      {nodes.length === 0 ? (
        <div className="panel-note">No edges registered.</div>
      ) : (
        <div className="ac-list">
          {nodes.map((n) => (
            <div key={n.node_id} className="ac-row">
              <div className="ac-row-main">
                <div className="ac-row-title">{n.node_id}</div>
                <div className="ac-row-meta">
                  {n.domain ? `domain: ${n.domain}` : "unassigned"} ·{" "}
                  {n.stale ? "stale" : "online"}
                </div>
              </div>
              <select
                className="ac-input narrow"
                value={n.domain || ""}
                disabled={busy || pending === n.node_id}
                onChange={(e) => assign(n.node_id, e.target.value)}
              >
                <option value="">— unassigned —</option>
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name || d.id}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
